import { useCallback, useRef, useState } from 'react';
import { PermissionsAndroid, Platform } from 'react-native';
import { Device, ScanMode } from 'react-native-ble-plx';
import { useRobotStateStore } from '../stores/useRobotStateStore';

export const useBleScanner = () => {
  const manager = useRobotStateStore((state) => state.BleManager);

  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [devices, setDevices] = useState<Device[]>([]);

  // Taken from https://github.com/dotintent/react-native-ble-plx/blob/master/INTRO.md
  const requestBluetoothPermission = useCallback(async () => {
    if (Platform.OS === 'ios') {
      return true;
    }

    if (
      Platform.OS === 'android' &&
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION
    ) {
      const apiLevel = parseInt(Platform.Version.toString(), 10);

      if (apiLevel < 31) {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      }

      if (
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN &&
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT
      ) {
        const result = await PermissionsAndroid.requestMultiple([
          PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
          PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
          PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
        ]);

        return (
          result['android.permission.BLUETOOTH_CONNECT'] ===
            PermissionsAndroid.RESULTS.GRANTED &&
          result['android.permission.BLUETOOTH_SCAN'] ===
            PermissionsAndroid.RESULTS.GRANTED &&
          result['android.permission.ACCESS_FINE_LOCATION'] ===
            PermissionsAndroid.RESULTS.GRANTED
        );
      }
    }

    return false;
  }, []);

  const scanDevicesTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const scanDevices = useCallback(
    (desiredServiceUUID: string) => {
      if (scanDevicesTimeout.current !== undefined) {
        return;
      }

      return new Promise<boolean>((resolve) => {
        if (!manager) {
          console.warn("No BleManager exists");
          resolve(false);
          return;
        }

        // Setup a timeout for the current
        clearTimeout(
          scanDevicesTimeout.current as ReturnType<typeof setTimeout>
        );
        scanDevicesTimeout.current = setTimeout(() => {
          resolve(true);
        }, 10000);

        setIsScanning(true);
        setDevices([]);

        manager.startDeviceScan(
          [desiredServiceUUID.toLowerCase()],
          {
            allowDuplicates: false,
            scanMode: ScanMode.LowLatency, // Used because this is running in foreground
          },
          (error, device) => {
            if (error) {
              console.warn('BLEError: ', JSON.stringify(error));
              resolve(false);
            }

            if (
              device &&
              device?.serviceUUIDs?.includes(desiredServiceUUID.toLowerCase())
            ) {
              setDevices((prev) => {
                if (!prev.map((d) => d.id).includes(device.id)) {
                  return [...prev, device];
                }
                return prev;
              });
            }
          }
        );
      }).finally(() => {
        setIsScanning(false);
        manager?.stopDeviceScan();
        scanDevicesTimeout.current = undefined;
      });
    },
    [manager]
  );

  const stopScanDevices = () => {
    if (isScanning) {
      clearTimeout(scanDevicesTimeout.current as ReturnType<typeof setTimeout>);
      manager?.stopDeviceScan();
    }
    setIsScanning(false);
  };

  return {
    isScanning,
    devices,
    requestBluetoothPermission,
    scanDevices,
    stopScanDevices,
  };
};
